// Remote players: roster (names, teams, public loadouts) and per-frame sync of the interpolated
// snapshot state into the renderer, plus their footsteps.

import { FOOTSTEP_RANGE } from "../audio";
import type { AudioEngine, MovementKind, ReloadPhase } from "../audio";
import type { BotLevel, TeamTag } from "../hud/types";
import type { InterpolatedPlayer } from "../net";
import type { PlayerInfo } from "../proto/gunrun/v1/game_pb";
import type {
  RemotePlayerInfo,
  RemotePlayerRenderState,
  Renderer,
} from "../render";
import { PlayerFlags } from "../wasm/types";
import type { Vec3Like } from "../wasm/types";
import { botLevel, teamTag } from "./convert";
import {
  handlingCues,
  motionCues,
  newHandlingState,
  newMotionState,
  remoteStepVolume,
} from "./audioCues";
import type { HandlingState, MotionState } from "./audioCues";
import type { FxDirector } from "./fx";
import { StrideTracker } from "./footsteps";

/** Third-person muzzle flash / fire animation flag lifetime after a shot (ms). */
const FIRING_MS = 100;
/** Remote footsteps are only worth a raycast within the distance the engine still plays them (m). */
const FOOTSTEP_CULL = FOOTSTEP_RANGE;
/** A player's hands: where reload and equip sounds come from, above the feet (m). */
const HAND_HEIGHT = 1.1;

export interface RemotePlayer {
  readonly id: number;
  name: string;
  team: TeamTag;
  /** Set for a server-side bot. */
  bot: BotLevel | null;
  enemy: boolean;
  level: number;
  /** Public loadout of the player (attachments of both guns). */
  primary: string;
  primaryAttachments: string[];
  primaryCamo: string;
  secondary: string;
  secondaryAttachments: string[];
  secondaryCamo: string;
  /** Last snapshot said ALIVE. */
  alive: boolean;
  /** Weapon shown on the model right now. */
  shownWeapon: string;
  firingUntil: number;
  deadSince: number;
  readonly stride: StrideTracker;
  readonly motion: MotionState;
  readonly handling: HandlingState;
  readonly renderInfo: RemotePlayerInfo;
  readonly state: RemotePlayerRenderState;
}

const ZERO: Vec3Like = { x: 0, y: 0, z: 0 };

export class RemotePlayers {
  readonly records = new Map<number, RemotePlayer>();
  private myId = -1;
  private myTeam: TeamTag = "none";
  private ffa = true;

  constructor(
    private readonly renderer: Renderer,
    private readonly audio: AudioEngine,
    private readonly fx: FxDirector,
  ) {}

  /** Identity of the local player; teams decide who is an enemy. */
  setLocal(id: number, team: TeamTag, ffa: boolean): void {
    this.myId = id;
    this.myTeam = team;
    this.ffa = ffa;
    for (const rec of this.records.values()) this.refreshEnemy(rec);
  }

  private refreshEnemy(rec: RemotePlayer): void {
    const enemy = this.isEnemy(rec);
    if (enemy === rec.enemy) return;
    rec.enemy = enemy;
    rec.renderInfo.enemy = enemy;
    rec.renderInfo.team = rec.team;
    this.renderer.players.upsert(rec.renderInfo);
  }

  /** Creates or updates a player from a Welcome / PlayerJoined / PlayerUpdated message. */
  upsert(info: PlayerInfo): RemotePlayer | null {
    if (info.id === this.myId) return null;
    const lo = info.loadout;
    const team = teamTag(info.team);
    let rec = this.records.get(info.id);
    const fresh = !rec;
    if (!rec) {
      const primary = lo?.primary ?? "";
      rec = {
        id: info.id,
        name: info.name,
        team,
        bot: botLevel(info.isBot, info.botDifficulty),
        enemy: true,
        level: info.level,
        primary,
        primaryAttachments: [],
        primaryCamo: "none",
        secondary: "",
        secondaryAttachments: [],
        secondaryCamo: "none",
        alive: false,
        shownWeapon: primary,
        firingUntil: 0,
        deadSince: -1,
        stride: new StrideTracker(),
        motion: newMotionState(),
        handling: newHandlingState(),
        renderInfo: {
          id: info.id,
          name: info.name,
          team,
          enemy: true,
          weaponId: primary,
          attachments: [],
          camo: "none",
        },
        state: {
          pos: ZERO,
          vel: ZERO,
          yaw: 0,
          pitch: 0,
          flags: 0,
          weaponId: primary,
          firing: false,
          deathTime: 0,
        },
      };
      this.records.set(info.id, rec);
    }
    rec.name = info.name;
    rec.team = team;
    rec.bot = botLevel(info.isBot, info.botDifficulty);
    rec.level = info.level;
    if (lo) {
      rec.primary = lo.primary;
      rec.primaryAttachments = [...lo.primaryAttachments];
      rec.primaryCamo = lo.primaryCamo || "none";
      rec.secondary = lo.secondary;
      rec.secondaryAttachments = [...lo.secondaryAttachments];
      rec.secondaryCamo = lo.secondaryCamo || "none";
    }
    this.applyWeapon(rec, fresh ? rec.primary : rec.shownWeapon);
    rec.renderInfo.enemy = this.isEnemy(rec);
    rec.enemy = rec.renderInfo.enemy;
    rec.renderInfo.name = rec.name;
    rec.renderInfo.team = rec.team;
    this.renderer.players.upsert(rec.renderInfo);
    return rec;
  }

  private isEnemy(rec: RemotePlayer): boolean {
    return this.ffa || rec.team === "none" || rec.team !== this.myTeam;
  }

  /** Points the render info at `weaponId` with the matching public attachments / camo. */
  private applyWeapon(rec: RemotePlayer, weaponId: string): void {
    rec.shownWeapon = weaponId;
    const info = rec.renderInfo;
    info.weaponId = weaponId;
    if (weaponId === rec.primary) {
      info.attachments = rec.primaryAttachments;
      info.camo = rec.primaryCamo;
    } else if (weaponId === rec.secondary) {
      info.attachments = rec.secondaryAttachments;
      info.camo = rec.secondaryCamo;
    } else {
      info.attachments = [];
      info.camo = "none";
    }
  }

  /** Attachments of the weapon a remote player is holding (for shot FX). */
  attachmentsOf(id: number, weaponId: string): string[] {
    const rec = this.records.get(id);
    if (!rec) return [];
    if (weaponId === rec.primary) return rec.primaryAttachments;
    if (weaponId === rec.secondary) return rec.secondaryAttachments;
    return [];
  }

  remove(id: number): void {
    if (this.records.delete(id)) this.renderer.players.remove(id);
  }

  clear(): void {
    this.records.clear();
    this.renderer.players.clear();
  }

  markFiring(id: number, nowMs: number): void {
    const rec = this.records.get(id);
    if (rec) rec.firingUntil = nowMs + FIRING_MS;
  }

  name(id: number): string | null {
    return this.records.get(id)?.name ?? null;
  }

  team(id: number): TeamTag {
    return this.records.get(id)?.team ?? "none";
  }

  /** Pushes the interpolated state of every sampled player to the renderer; `hiddenId` is not drawn (spectator camera too close). */
  sync(
    samples: ReadonlyMap<number, InterpolatedPlayer>,
    nowMs: number,
    dt: number,
    listener: Vec3Like,
    hiddenId: number | null,
  ): void {
    for (const p of samples.values()) {
      const rec = this.records.get(p.id);
      if (!rec) continue;
      if (p.weaponId !== rec.shownWeapon && p.weaponId !== "") {
        this.applyWeapon(rec, p.weaponId);
        this.renderer.players.upsert(rec.renderInfo);
      }
      const alive = (p.flags & PlayerFlags.ALIVE) !== 0;
      rec.alive = alive;
      let deathTime = 0;
      if (alive) {
        rec.deadSince = -1;
      } else {
        if (rec.deadSince < 0) rec.deadSince = nowMs;
        deathTime = Math.max(0.001, (nowMs - rec.deadSince) / 1000);
      }
      const s = rec.state;
      s.pos = p.pos;
      s.vel = p.vel;
      s.yaw = p.yaw;
      s.pitch = p.pitch;
      s.flags = p.flags;
      s.weaponId = rec.shownWeapon;
      s.firing = nowMs < rec.firingUntil;
      s.deathTime = deathTime;
      s.hidden = p.id === hiddenId;
      this.renderer.players.setState(p.id, s, dt);
      this.sounds(rec, p, dt, nowMs, listener);
    }
  }

  private readonly moves: MovementKind[] = [];
  private readonly handles: ReloadPhase[] = [];
  private readonly handPos: Vec3Like = { x: 0, y: 0, z: 0 };

  /** Everything a player makes audible: footsteps, movement and weapon-handling cues, all positional at the player. */
  private sounds(
    rec: RemotePlayer,
    p: InterpolatedPlayer,
    dt: number,
    nowMs: number,
    listener: Vec3Like,
  ): void {
    const moves = this.moves;
    moves.length = 0;
    motionCues(rec.motion, p.flags, p.vel.y, moves);
    for (const kind of moves) this.audio.playMovement(kind, p.pos, p.id);
    const handles = this.handles;
    handles.length = 0;
    handlingCues(rec.handling, p.flags, p.weaponId, nowMs, handles);
    if (handles.length > 0) {
      const hands = this.handPos;
      hands.x = p.pos.x;
      hands.y = p.pos.y + HAND_HEIGHT;
      hands.z = p.pos.z;
      for (const phase of handles) this.audio.playReload(phase, hands, p.id);
    }
    this.footsteps(rec, p, dt, listener);
  }

  private footsteps(
    rec: RemotePlayer,
    p: InterpolatedPlayer,
    dt: number,
    listener: Vec3Like,
  ): void {
    const speed = Math.hypot(p.vel.x, p.vel.z);
    const volume = remoteStepVolume(p.flags);
    if (volume === 0 || speed < 1) {
      rec.stride.reset();
      return;
    }
    if (!rec.stride.feed(speed * dt)) return;
    const dx = p.pos.x - listener.x;
    const dz = p.pos.z - listener.z;
    if (dx * dx + dz * dz > FOOTSTEP_CULL * FOOTSTEP_CULL) return;
    this.audio.playFootstep(this.fx.surfaceUnder(p.pos), p.pos, volume, p.id);
  }
}
